import React, { useState, useEffect } from 'react';
import { 
  Download, FileText, Filter, Calendar, TrendingUp, 
  PieChart, BarChart, IndianRupee, Clock, CheckCircle,
  AlertTriangle, RefreshCw, ExternalLink, Search
} from 'lucide-react';
import './AdminPayoutReports.css';
import { API_BASE_URL } from '../../api';

const AdminPayoutReports = () => {
  const [reportData, setReportData] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    payment_method: '',
    date_from: '',
    date_to: '',
    vendor_search: ''
  });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const API_BASE = `${API_BASE_URL}/api/admin/payouts`;

  const fetchReportData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams(filters);
      
      const response = await fetch(`${API_BASE}?${params}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setReportData(data);
      }
    } catch (error) {
      console.error('Error fetching report data:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async (format) => {
    try {
      setExporting(true);
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams({ ...filters, format });
      
      const response = await fetch(`${API_BASE}/payouts/export?${params}`, {
        headers: { 
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `payout-report-${Date.now()}.${format === 'excel' ? 'xlsx' : 'csv'}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Error exporting report:', error);
      alert('Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    fetchReportData();
  }, []);

  const formatCurrency = (amount) => 
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount || 0);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle size={16} className="status-icon status-success" />;
      case 'pending':
        return <Clock size={16} className="status-icon status-pending" />;
      case 'processing':
        return <RefreshCw size={16} className="status-icon status-processing" />;
      case 'rejected':
      case 'failed':
        return <AlertTriangle size={16} className="status-icon status-error" />;
      default:
        return <Clock size={16} className="status-icon status-warning" />;
    }
  };

  const calculateSummary = () => {
    if (!reportData?.payouts) return null;
    
    const payouts = reportData.payouts;
    const summary = {
      total_payouts: payouts.length,
      total_requested: payouts.reduce((sum, p) => sum + parseFloat(p.requested_amount || 0), 0),
      total_approved: payouts.reduce((sum, p) => sum + parseFloat(p.approved_amount || 0), 0),
      total_paid: payouts.filter(p => p.status === 'paid').reduce((sum, p) => sum + parseFloat(p.final_amount || 0), 0),
      status_breakdown: {}
    };

    payouts.forEach(payout => {
      summary.status_breakdown[payout.status] = (summary.status_breakdown[payout.status] || 0) + 1;
    });

    return summary;
  };

  const summary = calculateSummary();

  return (
    <div className="admin-payout-reports">
      <div className="reports-header">
        <div className="header-content">
          <h1 className="page-title">
            <FileText size={28} />
            Payout Reports & Analytics
          </h1>
          <p className="page-subtitle">Generate detailed reports and export payout data</p>
        </div>
        
        <div className="header-actions">
          <button 
            className="action-btn secondary"
            onClick={fetchReportData}
            disabled={loading}
          >
            <RefreshCw size={20} className={loading ? 'spinning' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters Section */}
      <div className="filters-section">
        <h3 className="section-title">
          <Filter size={20} />
          Filters & Export Options
        </h3>
        
        <div className="filters-grid">
          <div className="filter-group">
            <label>Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="processing">Processing</option>
              <option value="paid">Paid</option>
              <option value="rejected">Rejected</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Payment Method</label>
            <select
              value={filters.payment_method}
              onChange={(e) => setFilters(prev => ({ ...prev, payment_method: e.target.value }))}
            >
              <option value="">All Methods</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="upi">UPI</option>
              <option value="cheque">Cheque</option>
            </select>
          </div>

          <div className="filter-group">
            <label>From Date</label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
            />
          </div>

          <div className="filter-group">
            <label>To Date</label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilters(prev => ({ ...prev, date_to: e.target.value }))}
            />
          </div>

          <div className="filter-group">
            <label>Search Vendor</label>
            <div className="search-input">
              <input
                type="text"
                placeholder="Vendor name or email..."
                value={filters.vendor_search}
                onChange={(e) => setFilters(prev => ({ ...prev, vendor_search: e.target.value }))}
              />
              <Search size={16} className="search-icon" />
            </div>
          </div>
        </div>

        <div className="filter-actions">
          <button 
            className="action-btn primary"
            onClick={fetchReportData}
            disabled={loading}
          >
            <Search size={16} />
            Apply Filters
          </button>
          
          <button 
            className="action-btn secondary"
            onClick={() => setFilters({
              status: '',
              payment_method: '',
              date_from: '',
              date_to: '',
              vendor_search: ''
            })}
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="summary-section">
          <h3 className="section-title">
            <TrendingUp size={20} />
            Report Summary
          </h3>
          
          <div className="summary-grid">
            <div className="summary-card total">
              <div className="card-icon">
                <FileText size={24} />
              </div>
              <div className="card-content">
                <div className="card-value">{summary.total_payouts}</div>
                <div className="card-label">Total Payouts</div>
              </div>
            </div>

            <div className="summary-card requested">
              <div className="card-icon">
                <IndianRupee size={24} />
              </div>
              <div className="card-content">
                <div className="card-value">{formatCurrency(summary.total_requested)}</div>
                <div className="card-label">Total Requested</div>
              </div>
            </div>

            <div className="summary-card approved">
              <div className="card-icon">
                <CheckCircle size={24} />
              </div>
              <div className="card-content">
                <div className="card-value">{formatCurrency(summary.total_approved)}</div>
                <div className="card-label">Total Approved</div>
              </div>
            </div>

            <div className="summary-card paid">
              <div className="card-icon">
                <CheckCircle size={24} />
              </div>
              <div className="card-content">
                <div className="card-value">{formatCurrency(summary.total_paid)}</div>
                <div className="card-label">Total Paid</div>
              </div>
            </div>
          </div>

          {/* Status Breakdown */}
          <div className="status-breakdown">
            <h4>Status Breakdown</h4>
            <div className="status-grid">
              {Object.entries(summary.status_breakdown).map(([status, count]) => (
                <div key={status} className={`status-item ${status}`}>
                  {getStatusIcon(status)}
                  <span className="status-name">{status}</span>
                  <span className="status-count">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Export Section */}
      <div className="export-section">
        <h3 className="section-title">
          <Download size={20} />
          Export Reports
        </h3>
        
        <div className="export-options">
          <div className="export-card">
            <div className="export-icon">
              <FileText size={32} />
            </div>
            <div className="export-content">
              <h4>Excel Report</h4>
              <p>Comprehensive report with all payout details, vendor information, and transaction data</p>
              <button 
                className="export-btn primary"
                onClick={() => exportReport('excel')}
                disabled={exporting}
              >
                <Download size={16} />
                {exporting ? 'Exporting...' : 'Export Excel'}
              </button>
            </div>
          </div>

          <div className="export-card">
            <div className="export-icon">
              <FileText size={32} />
            </div>
            <div className="export-content">
              <h4>CSV Report</h4>
              <p>Simple comma-separated format for importing into other systems</p>
              <button 
                className="export-btn secondary"
                onClick={() => exportReport('csv')}
                disabled={exporting}
              >
                <Download size={16} />
                {exporting ? 'Exporting...' : 'Export CSV'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Table */}
      {reportData?.payouts && (
        <div className="preview-section">
          <h3 className="section-title">
            <BarChart size={20} />
            Data Preview ({reportData.payouts.length} records)
          </h3>
          
          <div className="preview-table-container">
            <table className="preview-table">
              <thead>
                <tr>
                  <th>Payout ID</th>
                  <th>Vendor</th>
                  <th>Requested</th>
                  <th>Final Amount</th>
                  <th>Status</th>
                  <th>Method</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {reportData.payouts.slice(0, 10).map(payout => (
                  <tr key={payout.id}>
                    <td>#{payout.id}</td>
                    <td>
                      <div className="vendor-info">
                        <div className="vendor-name">{payout.vendor_name}</div>
                        <div className="vendor-email">{payout.owner_email}</div>
                      </div>
                    </td>
                    <td>{formatCurrency(payout.requested_amount)}</td>
                    <td>{formatCurrency(payout.final_amount)}</td>
                    <td>
                      <div className="status-badge">
                        {getStatusIcon(payout.status)}
                        <span className={`status-text ${payout.status}`}>
                          {payout.status}
                        </span>
                      </div>
                    </td>
                    <td>{payout.payment_method}</td>
                    <td>{new Date(payout.requested_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {reportData.payouts.length > 10 && (
              <div className="preview-note">
                <p>Showing first 10 records. Export for complete data.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner">
            <RefreshCw size={32} className="spinning" />
            <p>Loading report data...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPayoutReports;
